import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SaveIcon, ArrowLeftIcon, HelpCircleIcon, CheckIcon, XIcon, ShieldIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
const UserForm: React.FC = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  // Mock user data for editing
  const userData = isEditing ? {
    fullName: 'John Smith',
    username: 'johnsmith',
    email: 'john@example.com',
    phone: '+1 (555) 123-4567',
    status: 'active',
    role: 'Admin',
    region: 'North America',
    permissions: {
      dashboard: ['view'],
      merchants: ['view', 'create', 'edit', 'delete'],
      users: ['view', 'create', 'edit', 'delete'],
      transactions: ['view', 'export'],
      reports: ['view', 'export', 'schedule'],
      settings: ['view', 'edit']
    }
  } : {
    fullName: '',
    username: '',
    email: '',
    phone: '',
    status: 'active',
    role: '',
    region: '',
    permissions: {
      dashboard: [],
      merchants: [],
      users: [],
      transactions: [],
      reports: [],
      settings: []
    }
  };
  const [user, setUser] = useState(userData);
  const [formStatus, setFormStatus] = useState<'draft' | 'submitted' | 'error'>('draft');
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setUser(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePermissionChange = (module: string, permission: string, checked: boolean) => {
    setUser(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: checked ? [...prev.permissions[module as keyof typeof prev.permissions], permission] : prev.permissions[module as keyof typeof prev.permissions].filter(p => p !== permission)
      }
    }));
  };
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value;
    // Preset permissions based on role
    let newPermissions = {
      ...user.permissions
    };
    if (role === 'Admin') {
      newPermissions = {
        dashboard: ['view'],
        merchants: ['view', 'create', 'edit', 'delete'],
        users: ['view', 'create', 'edit', 'delete'],
        transactions: ['view', 'export'],
        reports: ['view', 'export', 'schedule'],
        settings: ['view', 'edit']
      };
    } else if (role === 'Manager') {
      newPermissions = {
        dashboard: ['view'],
        merchants: ['view', 'create', 'edit'],
        users: ['view'],
        transactions: ['view', 'export'],
        reports: ['view', 'export'],
        settings: ['view']
      };
    } else if (role === 'Agent') {
      newPermissions = {
        dashboard: ['view'],
        merchants: ['view', 'create'],
        users: [],
        transactions: ['view'],
        reports: ['view'],
        settings: []
      };
    } else if (role === 'Auditor') {
      newPermissions = {
        dashboard: ['view'],
        merchants: ['view'],
        users: ['view'],
        transactions: ['view', 'export'],
        reports: ['view', 'export', 'schedule'],
        settings: []
      };
    }
    setUser(prev => ({
      ...prev,
      role,
      permissions: newPermissions
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Submit logic would go here
    setFormStatus('submitted');
    // Navigate back to user list
    setTimeout(() => {
      navigate('/users');
    }, 1500);
  };
  return <div className="space-y-6" data-id="element-7903">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-7904">
        <div data-id="element-7905">
          <button onClick={() => navigate('/users')} className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-7906">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-7907" />
            Back to Users
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1" data-id="element-7908">
            {isEditing ? 'Edit User' : 'New User'}
          </h1>
        </div>
        <div className="mt-3 sm:mt-0 flex items-center space-x-3" data-id="element-7909">
          {formStatus === 'submitted' && <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1 rounded-md text-sm flex items-center" data-id="element-7910">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-7911" />
              Submitted Successfully
            </div>}
          {formStatus === 'error' && <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-3 py-1 rounded-md text-sm flex items-center" data-id="element-7912">
              <XIcon className="h-4 w-4 mr-1" data-id="element-7913" />
              Error Submitting Form
            </div>}
          <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => navigate('/users')} data-id="element-7914">
            Cancel
          </button>
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSubmit} data-id="element-7915">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-7916" />
            {isEditing ? 'Update' : 'Create'} User
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit} data-id="element-7917">
        <Card title="User Information" data-id="element-7918">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7919">
            <div data-id="element-7920">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7921">
                Full Name <span className="text-red-500" data-id="element-7922">*</span>
              </label>
              <input type="text" name="fullName" value={user.fullName} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter full name" required data-id="element-7923" />
            </div>
            <div data-id="element-7924">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7925">
                Username <span className="text-red-500" data-id="element-7926">*</span>
              </label>
              <input type="text" name="username" value={user.username} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter username" required data-id="element-7927" />
            </div>
            <div data-id="element-7928">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7929">
                Email <span className="text-red-500" data-id="element-7930">*</span>
              </label>
              <input type="email" name="email" value={user.email} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter email address" required data-id="element-7931" />
            </div>
            <div data-id="element-7932">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7933">
                Phone
              </label>
              <input type="tel" name="phone" value={user.phone} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter phone number" data-id="element-7934" />
            </div>
            <div data-id="element-7935">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7936">
                Status <span className="text-red-500" data-id="element-7937">*</span>
              </label>
              <select name="status" value={user.status} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-7938">
                <option value="active" data-id="element-7939">Active</option>
                <option value="inactive" data-id="element-7940">Inactive</option>
                <option value="locked" data-id="element-7941">Locked</option>
              </select>
            </div>
            <div data-id="element-7942">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7943">
                Region <span className="text-red-500" data-id="element-7944">*</span>
              </label>
              <select name="region" value={user.region} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-7945">
                <option value="" data-id="element-7946">Select Region</option>
                <option value="North America" data-id="element-7947">North America</option>
                <option value="Europe" data-id="element-7948">Europe</option>
                <option value="Asia" data-id="element-7949">Asia</option>
                <option value="South America" data-id="element-7950">South America</option>
                <option value="Africa" data-id="element-7951">Africa</option>
                <option value="Oceania" data-id="element-7952">Oceania</option>
              </select>
            </div>
          </div>
        </Card>
        <div className="mt-6" data-id="element-7953">
          <Card title="Role & Permissions" data-id="element-7954">
            <div className="space-y-6" data-id="element-7955">
              <div data-id="element-7956">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7957">
                  Role <span className="text-red-500" data-id="element-7958">*</span>
                </label>
                <div className="flex items-center" data-id="element-7959">
                  <select name="role" value={user.role} onChange={handleRoleChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-7960">
                    <option value="" data-id="element-7961">Select Role</option>
                    <option value="Admin" data-id="element-7962">Admin</option>
                    <option value="Manager" data-id="element-7963">Manager</option>
                    <option value="Agent" data-id="element-7964">Agent</option>
                    <option value="Auditor" data-id="element-7965">Auditor</option>
                  </select>
                  <div className="ml-2" data-id="element-7966">
                    <HelpCircleIcon className="h-5 w-5 text-gray-400 cursor-help" title="Selecting a role will automatically set default permissions" data-id="element-7967" />
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-7968">
                <div className="flex items-center mb-4" data-id="element-7969">
                  <ShieldIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" data-id="element-7970" />
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-7971">
                    Permission Matrix
                  </h3>
                </div>
                <div className="overflow-x-auto" data-id="element-7972">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7973">
                    <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-7974">
                      <tr data-id="element-7975">
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-7976">
                          Module
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-7977">
                          View
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-7978">
                          Create
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-7979">
                          Edit
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-7980">
                          Delete
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-7981">
                          Export
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-7982">
                          Schedule
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7983">
                      {Object.keys(user.permissions).map(module => <tr key={module} data-id="element-7984">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-7985">
                            {module.charAt(0).toUpperCase() + module.slice(1)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7986">
                            <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('view')} onChange={e => handlePermissionChange(module, 'view', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7987" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7988">
                            {['dashboard', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-7989">
                                N/A
                              </span> : <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('create')} onChange={e => handlePermissionChange(module, 'create', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7990" />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7991">
                            {['dashboard', 'reports'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-7992">
                                N/A
                              </span> : <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('edit')} onChange={e => handlePermissionChange(module, 'edit', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7993" />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7994">
                            {['dashboard', 'transactions', 'reports', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-7995">
                                N/A
                              </span> : <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('delete')} onChange={e => handlePermissionChange(module, 'delete', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7996" />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7997">
                            {['dashboard', 'users', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-7998">
                                N/A
                              </span> : <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('export')} onChange={e => handlePermissionChange(module, 'export', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7999" />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-8000">
                            {module === 'reports' ? <input type="checkbox" checked={user.permissions.reports.includes('schedule')} onChange={e => handlePermissionChange('reports', 'schedule', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-8001" /> : <span className="text-gray-300 dark:text-gray-600" data-id="element-8002">
                                N/A
                              </span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Card>
        </div>
        {!isEditing && <div className="mt-6" data-id="element-8003">
            <Card title="Password" data-id="element-8004">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8005">
                <div data-id="element-8006">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-8007">
                    Password <span className="text-red-500" data-id="element-8008">*</span>
                  </label>
                  <input type="password" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter password" required={!isEditing} data-id="element-8009" />
                </div>
                <div data-id="element-8010">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-8011">
                    Confirm Password <span className="text-red-500" data-id="element-8012">*</span>
                  </label>
                  <input type="password" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Confirm password" required={!isEditing} data-id="element-8013" />
                </div>
              </div>
              <div className="mt-4" data-id="element-8014">
                <div className="flex items-center" data-id="element-8015">
                  <input id="force-reset" name="force-reset" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" defaultChecked data-id="element-8016" />
                  <label htmlFor="force-reset" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-8017">
                    Force user to change password on first login
                  </label>
                </div>
              </div>
            </Card>
          </div>}
      </form>
      {/* Form Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-8018">
        <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => navigate('/users')} data-id="element-8019">
          Cancel
        </button>
        <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSubmit} data-id="element-8020">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-8021" />
          {isEditing ? 'Update' : 'Create'} User
        </button>
      </div>
    </div>;
};
export default UserForm;